import { useTranslation } from 'react-i18next';

import { Patient } from '@/patient-app-common/types/Patient';
import { PatientDates } from '@/patient-app-common/hooks/usePatientDates';

import { EndCard } from './EndCard';
import { EndSurveyCard } from './EndSurveyCard';
import { HealthCheckCard } from './HealthCheckCard';
import { PreopCard } from './PreopCard';
import { PreopCardHL7 } from './PreopCardHL7';
import { PeriopCard } from './PeriopCard';
import { PostopCard } from './PostopCard';
import { PreopVisitCard } from './PreopVisitCard';

interface SurgicalProgramProps {
  patient: Patient;
  patientDates: PatientDates;
  programPhase: undefined | string;
}

export function SurgicalProgram({
  patient,
  patientDates,
  programPhase,
}: SurgicalProgramProps) {
  const hl7 = patient.surgicalDetails?.enrollmentType === 'auto_enrolled';
  const { preOpDate, surgeryDate, dischargeDate, startOfDayInHospitalTz } =
    patientDates;
  const { t } = useTranslation('home');
  const { firstName, othersUsingApp } = patient;
  const headerText = othersUsingApp
    ? t('common.text.greeting_name_indirect', { firstName })
    : t('common.text.greeting_name', { firstName });
  switch (programPhase) {
    case 'inPreop':
      if (hl7)
        return (
          <>
            <PreopVisitCard
              preOpDate={preOpDate}
              startOfDayInHospitalTz={startOfDayInHospitalTz}
            />
            <PreopCardHL7 patient={patient} />
          </>
        );
      return (
        <>
          <PreopVisitCard
            preOpDate={preOpDate}
            startOfDayInHospitalTz={startOfDayInHospitalTz}
          />
          <PreopCard
            patient={patient}
            surgeryDate={surgeryDate}
            startOfDayInHospitalTz={startOfDayInHospitalTz}
          />
        </>
      );

    case 'inPeriop':
      return (
        <PeriopCard
          patient={patient}
          surgeryDate={surgeryDate}
          startOfDayInHospitalTz={startOfDayInHospitalTz}
        />
      );

    case 'inPostop':
      return (
        <>
          <PostopCard
            patient={patient}
            dischargeDate={dischargeDate}
            startOfDayInHospitalTz={startOfDayInHospitalTz}
          />
          <HealthCheckCard patient={patient} patientDates={patientDates} />
        </>
      );

    case 'end':
      return (
        <>
          <EndCard headerText={headerText} />
          <EndSurveyCard
            endSurveyId={patient.endSurveyId}
            endSurveyCompleted={patient.endSurveyCompleted}
          />
        </>
      );

    default:
      return null;
  }
}
